import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import { getToolOptions, saveTool, toolingError } from "../../api/tooling";
import { downloadCsv } from "../../utils/funcs/csv";
import { ImportRow, validateImport } from "./toolingImport";
import { ErrorNotice } from "./ToolingShared";

export default function ToolingImportButton() {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        type="button"
        className="styled-btn--std"
        onClick={() => setShow(true)}
      >
        Bulk Import
      </button>
      {show && <ToolingImportModal onClose={() => setShow(false)} />}
    </>
  );
}

export function ToolingImportModal({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const options = useQuery({
    queryKey: ["tooling", "options"],
    queryFn: getToolOptions,
  });
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [error, setError] = useState("");
  const [reading, setReading] = useState(false);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [results, setResults] = useState<Record<number, string>>({});
  const busy = useRef(false);
  const invalid = rows.some((r) => r.errors.length);
  const imported = Object.values(results).filter((r) =>
    r.startsWith("Imported")
  ).length;

  const run = async () => {
    if (busy.current || started || !rows.length || invalid) return;
    busy.current = true;
    setRunning(true);
    setStarted(true);
    try {
      for (const row of rows) {
        try {
          const tool = await saveTool(row.input);
          setResults((prev) => ({
            ...prev,
            [row.row]: `Imported${tool?.code ? `: ${tool.code}` : ""}`,
          }));
        } catch (err) {
          setResults((prev) => ({
            ...prev,
            [row.row]: `Check required: ${toolingError(err)}`,
          }));
          setError(
            `Import stopped at CSV row ${row.row}. Earlier successful rows are saved. Check the tooling list before importing this row again, as a connection error can occur after saving. Later rows were not imported.`
          );
          break;
        }
      }
    } finally {
      await client.invalidateQueries({ queryKey: ["tooling"] });
      setRunning(false);
      busy.current = false;
    }
  };

  return (
    <Modal
      show
      size="xl"
      backdrop="static"
      keyboard={!running && !reading}
      onHide={() => !running && !reading && onClose()}
    >
      <Modal.Header closeButton={!running && !reading}>
        <Modal.Title>Bulk Import Toolings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorNotice error={options.error} retry={() => options.refetch()} />
        <p>
          Download the CSV template, fill in one tooling per row, then upload it
          for review. This creates new toolings; tool codes are assigned
          automatically.
        </p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            variant="outline-primary"
            onClick={() =>
              downloadCsv(
                [
                  {
                    type: "",
                    operation: "",
                    part_ids: "",
                    location_id: "",
                    details: "",
                  },
                ],
                "tooling_import_template.csv"
              )
            }
          >
            Download Template
          </Button>
          <Button
            variant="outline-secondary"
            disabled={!options.data}
            onClick={() =>
              options.data &&
              downloadCsv(
                [
                  ...options.data.types.map((t) => ({
                    category: "Tool type",
                    value: t.value,
                    label: t.label,
                  })),
                  ...options.data.parts.map((p) => ({
                    category: "Part",
                    value: p.value,
                    label: p.label,
                  })),
                  ...(options.data.locations || []).map((l) => ({
                    category: "Location",
                    value: l.value,
                    label: l.label,
                  })),
                ],
                "tooling_import_reference.csv"
              )
            }
          >
            Download Types, Parts & Locations
          </Button>
        </div>
        <ul className="small text-muted">
          <li>
            Type: use a tool type from the reference file. Operation: cutting or
            bending.
          </li>
          <li>
            Part IDs: at least one linked part is required; separate multiple
            IDs with a semicolon (for example, 12;34).
          </li>
          <li>
            Location ID and details are optional. Save your completed
            spreadsheet as CSV (.csv).
          </li>
          <li>
            Import each file only once to avoid creating duplicate toolings.
          </li>
        </ul>
        <Form.Group controlId="tooling-import-file" className="mb-3">
          <Form.Label>Completed template</Form.Label>
          <Form.Control
            type="file"
            accept=".csv,text/csv"
            disabled={!options.data || reading || running || started}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              const file = event.target.files?.[0];
              setRows([]);
              setError("");
              setResults({});
              if (!file || !options.data) return;
              if (!file.name.toLowerCase().endsWith(".csv")) {
                setError("Please upload a CSV file.");
                return;
              }
              if (file.size > 10 * 1024 * 1024) {
                setError(
                  "Please split files larger than 10 MB into smaller CSV files."
                );
                return;
              }
              setReading(true);
              try {
                setRows(validateImport(await file.text(), options.data));
              } catch (err) {
                setError(toolingError(err));
              } finally {
                setReading(false);
              }
            }}
          />
        </Form.Group>
        {reading && <p role="status">Reading and validating file...</p>}
        {error && <Alert variant="danger">{error}</Alert>}
        {rows.length > 0 && (
          <>
            <p role="status">
              {started
                ? `${imported} of ${rows.length} toolings imported.`
                : `${rows.length} toolings found. ${
                    invalid
                      ? "Fix the errors and upload the corrected file."
                      : "Ready to import."
                  }`}
            </p>
            <div style={{ maxHeight: 350, overflow: "auto" }}>
              <Table size="sm" bordered striped>
                <thead>
                  <tr>
                    <th>CSV row</th>
                    <th>Type</th>
                    <th>Operation</th>
                    <th>Part IDs</th>
                    <th>Location ID</th>
                    <th>Details</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.row}>
                      <td>{row.row}</td>
                      <td>{row.input.type}</td>
                      <td>{row.input.operation}</td>
                      <td>{row.input.part_ids.join("; ")}</td>
                      <td>{row.input.location_id ?? "—"}</td>
                      <td style={{ maxWidth: 250, overflowWrap: "anywhere" }}>
                        {row.input.details}
                      </td>
                      <td className={row.errors.length ? "text-danger" : ""}>
                        {row.errors.join("; ") ||
                          results[row.row] ||
                          (started ? "Not imported yet" : "Ready")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
        {running && (
          <p className="mt-3">
            Importing toolings. Keep this window open until the import finishes.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={running || reading}
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          disabled={!rows.length || invalid || reading || running || started}
          onClick={run}
        >
          {running
            ? "Importing..."
            : `Import${rows.length ? ` ${rows.length} toolings` : ""}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
